import DataAccuracyTable from "./DataAccuracyTable/DataAccuracyTable";
import {
  Autocomplete,
  Button,
  Chip,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  selectUserData,
  selectUserToken,
} from "../../../../features/userSlice";
import { DataAccuracyEndPointLogic } from "../../../../logic/AssetManagement/Health/Data Accuracy/dataAccuracyEndPointLogic";
import store from "../../../../store";
import SimpleBackdrop from "../../../BackDrop/BackDrop";
import { useDispatch } from "react-redux";
import { Box } from "@mui/system";
import CancelIcon from "@mui/icons-material/Cancel";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import FloatingActionItems from "../../../../pages/FloatingItems/FloatingActionItems";
//test

function DataAccuracy() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, seLoading] = useState(false);
  const [loadingOrganization, setLoadingOrganization] = useState(false);
  const authToken = useSelector(selectUserToken);
  const dispatch = useDispatch();
  const { allDimensions } = useSelector((state: any) => state.dataAccuracy);
  // Read from query params
  const initialFetchBy = searchParams.get("fetchBy") || "Website";
  const initialSelected = searchParams.get("selected")?.split(",") || [];

  const [fetchBy, setFetchBy] = useState(initialFetchBy);
  const [selectedOptions, setSelectedOptions] =
    useState<any[]>(initialSelected);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const siteAndDimensionsList = useSelector(selectUserData);
  const dimensionList = siteAndDimensionsList?.data?.dimensions;
  const siteList = siteAndDimensionsList?.data?.website_permissions;
  const userPermittedOrganizationName =
    siteAndDimensionsList?.data?.user_details?.organization_name;
  const allDimenssionList = allDimensions.filter(
    (dimension: any) =>
      dimension.organization_id__organization_name ===
      userPermittedOrganizationName,
  );

  const filteredDimensionList = allDimenssionList.map((dimension: any) => {
    return dimension.dimension;
  });
  const theme = useTheme();

  // In DataAccuracy.tsx
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (fetchBy) params.set("fetchBy", fetchBy);
    if (selectedOptions.length > 0) {
      params.set("selected", selectedOptions.join(","));
    } else {
      params.delete("selected");
    }
    // Preserve other params like 'page'
    setSearchParams(params);
    //  DataAccuracyEndPointLogic.getAllUserList(authToken);
    // eslint-disable-next-line
  }, [fetchBy, selectedOptions]);

  const getAutocompleteOptions = () => {
    switch (fetchBy) {
      case "Website":
        return siteList.map((siteObj: any) => {
          // Extract the first key (UUID) and site data
          const siteId = Object.keys(siteObj)[0];
          const siteData = siteObj[siteId];
          return {
            value: siteId,
            label:
              siteData.ui_site_name || siteData.site_url || "Unnamed Website",
          };
        });
      case "Dimension":
        return dimensionList.map((dimension: string) => ({
          value: dimension,
          label: dimension,
        }));
      case "Organization":
        return []; // Return empty array when Organization is selected
      default:
        return [];
    }
  };
  const autocompleteOptions = getAutocompleteOptions();
  const isOrganizationSelected = fetchBy === "Organization";

  const type = fetchBy === "Website" ? "site" : "dimension";
  const queryId = 5646947083878400;

  useEffect(() => {
    // Clean up URL params first
    const newParams = new URLSearchParams(searchParams);
    let changed = false;

    if (newParams.has("taskTitle")) {
      newParams.delete("taskTitle");
      changed = true;
    }
    if (newParams.has("reference_id")) {
      newParams.delete("reference_id");
      changed = true;
    }
    if (newParams.has("task_structure_shared_uid")) {
      newParams.delete("task_structure_shared_uid");
      changed = true;
    }
    if (newParams.has("asset_shared_uids")) {
      newParams.delete("asset_shared_uids");
      changed = true;
    }

    if (changed) {
      setSearchParams(newParams, { replace: true });
    }

    // Then fetch data if conditions are met
    if (
      initialSelected.length > 0 &&
      autocompleteOptions.length > 0 &&
      !hasFetchedData
    ) {
      handleGetData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetData = async () => {
    seLoading(true);
    try {
      await DataAccuracyEndPointLogic.getDataAccuracyByWebsiteOrDimensionOrOrganization(
        queryId,
        authToken,
        type,
        type === "site" ? selectedOptions : [Object.keys(siteList[0])[0]],
        fetchBy === "Dimension"
          ? selectedOptions
          : fetchBy === "Organization"
            ? filteredDimensionList // Send all dimensions for Organization
            : [dimensionList[0]],
        dispatch,
      );
      await DataAccuracyEndPointLogic.getAllUserList(authToken);
    } catch (error) {
      store.dispatch({ type: "error", payload: { message: error } });
    } finally {
      seLoading(false);
      setHasFetchedData(true);
    }
  };

  const fetchAllOrganizationData = async () => {
    try {
      setLoadingOrganization(true);
      await DataAccuracyEndPointLogic.getAllDimensions(authToken, dispatch);
    } catch (error) {
      store.dispatch({ type: "error", payload: { message: error } });
    } finally {
      setLoadingOrganization(false);
    }
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        {(loading || loadingOrganization) && <SimpleBackdrop />}
        <Paper sx={{ p: 2 }}>
          {/* Header & Fetch By - Side by Side */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography fontSize={24}>Data Accuracy Tasks</Typography>
              <Typography fontSize={14} color="#00000099" sx={{ mt: 0.5 }}>
                Monitor, analyze, and mitigate data irregularities quickly with
                an actionable task list.
              </Typography>
            </Box>

            {/* Fetch By Options */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: { xs: 2, md: 0 },
              }}
            >
              <Typography sx={{ mr: 2 }}>Fetch by:</Typography>
              <RadioGroup
                row
                value={fetchBy}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFetchBy(newValue);
                  setSelectedOptions([]); // Clear selected options when changing fetchBy
                  if (newValue === "Organization") {
                    // Calling all dimensions api
                    fetchAllOrganizationData();
                  }
                }}
              >
                <FormControlLabel
                  value="Website"
                  control={<Radio color="secondary" />}
                  label="Website"
                  data-tracker="409"
                />
                <FormControlLabel
                  value="Dimension"
                  control={<Radio color="secondary" />}
                  label="Dimension"
                  disabled
                  data-tracker="408"
                />
                <FormControlLabel
                  value="Organization"
                  control={<Radio color="secondary" />}
                  label="Organization"
                  disabled
                  data-tracker="410"
                />
              </RadioGroup>
            </Box>
          </Box>
          {/* Autocomplete and Get Data Button - Side by Side */}
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 4,
              mt: 3,
            }}
          >
            <Autocomplete
              multiple
              size="small"
              disabled={isOrganizationSelected}
              options={[
                // Show "Select All" only if not all are selected
                ...(selectedOptions.length < autocompleteOptions.length
                  ? [{ label: "Select All", value: "ALL" }]
                  : []),
                ...autocompleteOptions,
              ]}
              getOptionLabel={(option: any) => option.label}
              isOptionEqualToValue={(option: any, value: any) =>
                option.value === value.value
              }
              value={selectedOptions
                .map((val) =>
                  autocompleteOptions.find((opt: any) => opt.value === val),
                )
                .filter(Boolean)}
              onChange={(_, val) => {
                if (val.some((v: any) => v.value === "ALL")) {
                  // If Select All is clicked
                  setSelectedOptions(
                    autocompleteOptions.map((opt: any) => opt.value),
                  );
                } else {
                  setSelectedOptions(
                    val ? val.map((option: any) => option.value) : [],
                  );
                }
                setHasFetchedData(false);
              }}
              filterOptions={(options) => {
                // Remove selected items from dropdown
                return options.filter(
                  (opt: any) =>
                    opt.value === "ALL" || !selectedOptions.includes(opt.value),
                );
              }}
              noOptionsText="No options left to select"
              sx={{ minWidth: 300, flexGrow: 1 }}
              renderTags={(value, getTagProps) =>
                value.map((option: any, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    label={option.label}
                    color="secondary"
                    sx={{
                      backgroundColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.contrastText,
                      height: 24,
                    }}
                    deleteIcon={<CancelIcon />}
                  />
                ))
              }
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  label="Select Website or Dimension"
                  color="secondary"
                  InputLabelProps={{ shrink: true }}
                />
              )}
              ListboxProps={{
                sx: {
                  '& .MuiAutocomplete-option[aria-selected="true"]': {
                    backgroundColor: "#fbf7fc",
                    "&.Mui-focused": {
                      backgroundColor: "#f3edf8",
                    },
                  },
                  "& .MuiAutocomplete-option.Mui-focused": {
                    backgroundColor: "#fbf7fc",
                  },
                },
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{
                    color:
                      option.value === "ALL"
                        ? theme.palette.secondary.main
                        : "inherit",
                    fontWeight: option.value === "ALL" ? 500 : 400,
                  }}
                >
                  {option.label}
                </li>
              )}
            />

            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{ textTransform: "none", height: 40, minWidth: 116 }}
              onClick={() => {
                handleGetData();
              }}
              disabled={
                loadingOrganization ||
                (fetchBy !== "Organization" && selectedOptions.length === 0)
              }
            >
              GET DATA
            </Button>
          </Box>
        </Paper>
        {/* Loader (covers entire section) */}
        {loading && <SimpleBackdrop />}

        {/* Result Section - Only Render When Data Exists AND Not Loading */}
        {hasFetchedData && !loading && (
          <Paper sx={{ p: "10px 16px", mt: "10px" }}>
            <DataAccuracyTable />

            {selectedOptions?.length > 0 && (
              <div data-tracker="403">
                <FloatingActionItems
                  selectedSiteFloatingAction={selectedOptions[0]}
                  selectedDimensionsFloatingAction={""}
                  trackIdFloatingAction={"432"}
                />
              </div>
            )}
          </Paper>
        )}
      </Box>
    </>
  );
}

export default DataAccuracy;
